import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { CalendarMonth, List } from '@mui/icons-material';
// import { AccessTime } from '@mui/icons-material';
import { add, addDays, startOfWeek, addWeeks, endOfWeek } from 'date-fns';
import { Ticket, TicketDirectory } from 'types/User/firebaseTypes/ticket';
import { DeliveryDetailsModal } from './modals/DeliveryDetailsModal/DeliveryDetailsModal';
import { DeliveriesWeekView } from './views/DeliveriesWeekView/DeliveriesWeekView';
import { UnderConstructionPlaceholder } from 'components/UnderConstructionPlaceholder/UnderConstructionPlaceholder';
import { BulkStatusModal } from './modals/BulkStatusModal';
import {
    filterDeliveriesByStatus,
    normalizeDeliveryTicket,
    searchDeliveries,
} from 'helpers/planning';
import { PlanningToolbar } from './common/PlanningToolbar';
import { DeliveriesPrintModal } from './modals/DeliveriesPrintModal/DeliveriesPrintModal';
import { CreateDeliveryModal } from './modals/CreateDeliveryModal/CreateDeliveryModal';
import {
    useLazyGetDeliveryTicketsPageQuery,
    useLazyUpdateDeliveryTicketQuery,
} from 'store/services/planning';
import { activateSnackbar } from 'store/features/ui';
import { RootState, useAppDispatch } from 'store';
import DeliveriesListView from './views/DeliveriesListView/DeliveriesListView';
import { Device } from 'types';
import { findIndex, forEach, keyBy, uniq } from 'lodash';
import { useLazyGetDeviceByDeviceIdEsQuery } from 'store/services';
import { TuTabs } from 'components/common/TuTabs/TuTabs';
import { TuTab } from 'components/common/TuTab/TuTab';
import { ResolveDeliveryModal } from './modals/ResolveDeliveryModal/ResolveDeliveryModal';
import { useLazyGetIssueTicketsPageQuery } from 'store/services/issue-tickets';
import styles from './Planning.module.scss';

export interface PlanningProps {
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
}

export type DeviceLookup = {
    /** Long device id mapped to device. */
    [key: string]: Device & { activeIssueTicket?: Ticket };
};

export const Planning = ({ viewAsSupplier }: PlanningProps) => {
    const location = useLocation();
    const dispatch = useAppDispatch();

    const apiFetchInProgress = useRef(false);
    const componentUnmounted = useRef(false);

    const authentication = useSelector(
        (state: RootState) => state.authentication
    );

    const [getDevicesQuery, { isLoading: isDeviceListLoading }] =
        useLazyGetDeviceByDeviceIdEsQuery();

    const supplierId =
        authentication?.user &&
        (viewAsSupplier ??
            authentication.supplierId ??
            authentication?.user?.protected?.supplierId);
    const [currentView, setCurrentView] = useState<string>('list');

    // deliveries for which I am the driver
    const [shouldOnlyShowMyTickets, setShouldOnlyShowMyTickets] =
        useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    // TODO: connect filters to user session.
    const [statusFilters, setStatusFilters] = useState<Record<string, boolean>>(
        {
            done: true,
            in_progress: false,
            backlog: true,
            planned: false,
            assigned: true,
            archived: false,
        }
    );

    // Modal controls.
    const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
    const [isBulkModalOpen, setIsBulkModalOpen] = useState<boolean>(false);
    const [isPrintModalOpen, setIsPrintModalOpen] = useState<boolean>(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] =
        useState<boolean>(false);
    const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);

    const [ticketsToResolve, setTicketsToResolve] = useState<Ticket[]>([]);

    // Which ticket to show on the details modal
    const [activeTicket, setActiveTicketForModal] = useState<Ticket | null>(
        null
    );

    // deliveries in state (full list of supplier deliveries)
    const [allDeliveries, setAllDeliveries] = useState<Ticket[]>([]);

    // pared-down deliveries
    const [deliveries, setDeliveries] = useState<Ticket[]>(allDeliveries);

    // date range
    const [minDate, setMinDate] = useState<Date>(
        add(startOfWeek(new Date()), { days: 1 })
    );
    const [maxDate, setMaxDate] = useState<Date>(
        add(endOfWeek(new Date()), { days: 1 })
    );

    // devices reference list - because we aren't updating device objects within tickets
    const [devicesList, setDevicesList] = useState<DeviceLookup>({});

    // checkbox selection
    const [selectedTickets, setSelectedTickets] = useState<Array<string>>([]);

    const [getDeliveryTicketsPageQuery] = useLazyGetDeliveryTicketsPageQuery();
    const [updateDeliveryQuery] = useLazyUpdateDeliveryTicketQuery();
    const [getIssueTicketsQuery, { isLoading: isIssuesLoading }] =
        useLazyGetIssueTicketsPageQuery();

    // Handle tab changes.
    useEffect(() => {
        const urlParts = location.pathname.split('/');
        let currentTab = urlParts.pop() ?? '';

        const planningTabs = ['list', 'day', 'week'];
        // If the last part of the URL does not match a valid tab/sub page, default to the list tab.
        if (!planningTabs.includes(currentTab)) {
            currentTab = 'list';
        }

        setCurrentView(currentTab);
    }, [location.pathname]);

    // Fetch tickets when the component mounts
    useEffect(() => {
        // Make sure to update this ref when the component mounts and unmounts
        componentUnmounted.current = false;

        // function to fetch all pages of tickets from napi endpoint via RTK Query
        const fetchAllDeliveriesPaginated = async ({
            supplierId,
            ticketDirectory,
        }: {
            supplierId: string;
            ticketDirectory: TicketDirectory;
        }) => {
            // recursive function to fetch each page of tickets
            const fetchPage = (startAt?: string) => {
                return new Promise<void>((resolve, reject) => {
                    let apiPromise;

                    // if startAt is defined, then we're fetching a subsequent page
                    if (startAt) {
                        apiPromise = getDeliveryTicketsPageQuery({
                            supplierId,
                            ticketDirectory,
                            startAt,
                        });
                    } else {
                        // otherwise, we're fetching the first page
                        apiPromise = getDeliveryTicketsPageQuery({
                            supplierId,
                            ticketDirectory,
                        });
                    }

                    apiPromise
                        .then((result) => {
                            let atLimit = false;
                            // If the component unmounted before this promise resolved, then we don't want to update state
                            if (componentUnmounted.current) {
                                resolve();
                                return;
                            }

                            const tickets = result?.data?.tickets;
                            const nextStartAt = result?.data?.nextStartAt;

                            // If tickets exist, then we can update the table data
                            if (tickets) {
                                // format tickets as an array
                                const ticketValues: Ticket[] =
                                    Object.values(tickets);

                                // update delivery list (without overwriting whats already there)
                                setAllDeliveries((prevTickets) => {
                                    if (prevTickets) {
                                        const allTickets = [
                                            ...prevTickets,
                                            ...ticketValues,
                                        ];
                                        if (allTickets.length > 10000)
                                            atLimit = true;
                                        return allTickets;
                                    } else {
                                        return ticketValues;
                                    }
                                });

                                const deviceIds = uniq(
                                    ticketValues.map(
                                        (ticket) => ticket.deviceId
                                    )
                                );

                                getDevicesQuery({
                                    user: authentication.user,
                                    deviceId: deviceIds.join(' '),
                                    limit: deviceIds.length,
                                }).then((deviceResult) => {
                                    if (deviceResult?.data?.results) {
                                        setDevicesList((prevList) => ({
                                            ...prevList,
                                            ...keyBy(
                                                deviceResult?.data?.results,
                                                (device) => device.deviceId
                                            ),
                                        }));
                                    }
                                });
                            }

                            // recursively fetch more data if there was a nextStartAt key returned
                            if (nextStartAt && !atLimit) {
                                fetchPage(nextStartAt)
                                    .then(() => {
                                        resolve();
                                    })
                                    .catch((error) => {
                                        reject(error);
                                    });
                                // if no nextStartAt key, then we're done fetching
                            } else {
                                resolve();
                            }

                            // Throw error if api call fails
                        })
                        .catch((error) => {
                            reject(error);
                        });
                });
            };

            // initiate recursive function and return promise
            return fetchPage();
        };

        // function to fetch tickets and handle component states asynchronously
        const fetchTickets = async () => {
            // If an API call is already in progress, return early
            if (apiFetchInProgress.current) {
                return;
            }

            try {
                // if valid supplier id, fetch tickets
                if (supplierId || supplierId?.toString() === '0') {
                    // set flag that we are starting a api fetch (prevent multiple fetches when useEffect is triggered multiple times)
                    apiFetchInProgress.current = true;

                    // make sure we reset table data to before we start fetching
                    //setTableData(null);

                    // fetch active issue tickets
                    await fetchAllDeliveriesPaginated({
                        supplierId: supplierId,
                        ticketDirectory: 'active',
                    });

                    // fetch resolved issue tickets
                    await fetchAllDeliveriesPaginated({
                        supplierId: supplierId,
                        ticketDirectory: 'resolved',
                    });
                }
            } catch (error) {
                // Show snackbar with error message when fetch fails
                dispatch(
                    activateSnackbar({
                        message: JSON.stringify(error),
                        severity: 'error',
                    })
                );
            }

            // After we finish fetching, reset flags
            apiFetchInProgress.current = false;
        };

        // initiate ticket fetch async
        fetchTickets();

        // return a callback that sets flag to true when component unmounts
        // this will cause any ongoing API calls to abort
        return () => {
            componentUnmounted.current = true;
        };
    }, [
        dispatch,
        getDeliveryTicketsPageQuery,
        supplierId,
        viewAsSupplier,
        authentication,
        getDevicesQuery,
    ]);

    // After we're done loading, fetch active issues for alerting purposes
    useEffect(() => {
        if (
            !apiFetchInProgress.current &&
            allDeliveries.length &&
            !isDeviceListLoading &&
            supplierId
        ) {
            const fetchIssues = (startAt?: string) => {
                const apiPromise = startAt
                    ? getIssueTicketsQuery({
                          supplierId,
                          startAt,
                          ticketDirectory: 'active',
                      })
                    : getIssueTicketsQuery({
                          supplierId,
                          ticketDirectory: 'active',
                      });

                apiPromise.then((result) => {
                    if (result?.data?.tickets) {
                        setDevicesList((prevList) => {
                            const newList = { ...prevList };
                            forEach(result?.data?.tickets, (issue) => {
                                const deviceWithIssue = newList[issue.deviceId];
                                if (
                                    deviceWithIssue &&
                                    !deviceWithIssue.activeIssueTicket
                                ) {
                                    newList[issue.deviceId] = {
                                        ...deviceWithIssue,
                                        activeIssueTicket: issue,
                                    };
                                }
                            });

                            return newList;
                        });
                    }
                    if (result?.data?.nextStartAt) {
                        fetchIssues(result.data.nextStartAt);
                    }
                });
            };
            fetchIssues();
        }
    }, [getIssueTicketsQuery, supplierId, isDeviceListLoading, allDeliveries]);

    // Filter tickets. Check if we only want my deliveries or if we have a search term to filter by.
    useEffect(() => {
        if (allDeliveries?.length && authentication) {
            if (shouldOnlyShowMyTickets || searchTerm || statusFilters) {
                const searchFilteredDeliveries = searchDeliveries(
                    allDeliveries,
                    searchTerm,
                    devicesList,
                    shouldOnlyShowMyTickets && authentication.user?.uid
                        ? authentication.user.uid
                        : ''
                );

                const { assigned, ...ticketStatusFilters } = statusFilters;

                const statusFilteredDeliveries = filterDeliveriesByStatus(
                    ticketStatusFilters,
                    searchFilteredDeliveries
                );

                if (assigned) {
                    const assignedTickets = statusFilteredDeliveries.filter(
                        (ticket) => !!ticket?.taggedUsers?.[0]?.uid
                    );

                    return setDeliveries(assignedTickets);
                }

                setDeliveries(statusFilteredDeliveries);
            } else {
                setDeliveries(allDeliveries);
            }
        }
    }, [
        allDeliveries,
        setDeliveries,
        authentication,
        shouldOnlyShowMyTickets,
        searchTerm,
        statusFilters,
        devicesList,
    ]);

    const setActiveTicket = (ticket: Ticket | null) => {
        setActiveTicketForModal(ticket);
        setIsDetailsModalOpen(true);
    };

    const changeTab = (tabName: string) => {
        setCurrentView(tabName);
        setSelectedTickets([]);
        if (!viewAsSupplier)
            window.history.replaceState(null, '', `/planning/${tabName}`);
        else
            window.history.replaceState(
                null,
                '',
                `/supplier-details/${viewAsSupplier}/planning/${tabName}`
            );
    };

    const changeWeek = (direction: number) => {
        setMinDate((prevMin) =>
            addDays(startOfWeek(addWeeks(prevMin, direction)), 1)
        );
    };

    const bulkUpdateStatuses = (status: string) => {
        const newTicketsList: Array<Ticket> = activeTicket
            ? [activeTicket]
            : selectedTickets.map((ticket) => {
                  return {
                      ...deliveries.find((delivery) => delivery.id === ticket),
                      status,
                  } as Ticket;
              });

        if (status === 'done') {
            setTicketsToResolve(newTicketsList);
            setIsResolveModalOpen(true);

            return;
        }

        if (
            newTicketsList?.length &&
            (supplierId || supplierId?.toString() === '0')
        ) {
            return Promise.all(
                newTicketsList.map((ticket) =>
                    updateDeliveryQuery({
                        supplierId,
                        deviceId: ticket.deviceId,
                        newTicket: normalizeDeliveryTicket(ticket),
                    })
                )
            )
                .then((results) => {
                    const resultTickets = results.map((res) => res.data);
                    const newAllDeliveries = [...allDeliveries];
                    resultTickets.forEach((ticket) => {
                        if (!ticket) return;

                        const ticketLocation = newAllDeliveries.findIndex(
                            (delivery) => delivery.id === ticket.id
                        );
                        if (ticketLocation > -1) {
                            newAllDeliveries.splice(ticketLocation, 1, ticket);
                        }
                    });

                    setAllDeliveries(newAllDeliveries);
                })
                .finally(() => setSelectedTickets([]));
        }
    };

    return (
        <div data-testid="Planning" className={styles.container}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TuTabs
                    value={currentView}
                    aria-label="Change view"
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {/* <Tab
                        label="Day View"
                        value="day"
                        onClick={() => changeTab('day')}
                        icon={<AccessTime />}
                        data-testid="DayViewTab"
                    /> */}
                    <TuTab
                        label="Week View"
                        value="week"
                        onClick={() => changeTab('week')}
                        icon={<CalendarMonth />}
                        data-testid="WeekViewTab"
                    />
                    <TuTab
                        label="List View"
                        value="list"
                        onClick={() => changeTab('list')}
                        icon={<List />}
                        data-testid="ListViewTab"
                    />
                </TuTabs>
            </Box>
            <PlanningToolbar
                currentView={currentView}
                minDate={minDate}
                maxDate={maxDate}
                shouldOnlyShowMyTickets={shouldOnlyShowMyTickets}
                setMinDate={setMinDate}
                setMaxDate={setMaxDate}
                setShouldOnlyShowMyTickets={setShouldOnlyShowMyTickets}
                setIsCreateModalOpen={setIsCreateModalOpen}
                changeWeek={changeWeek}
                setSearchTerm={setSearchTerm}
                setIsPrintModalOpen={setIsPrintModalOpen}
                statusFilters={statusFilters}
                setStatusFilters={setStatusFilters}
            />
            {currentView === 'list' && (
                <DeliveriesListView
                    minDate={minDate}
                    maxDate={maxDate}
                    deliveries={deliveries}
                    selectedTickets={selectedTickets}
                    setSelectedTickets={setSelectedTickets}
                    supplierId={supplierId ?? null}
                    setActiveTicket={setActiveTicket}
                    setIsDetailsModalOpen={setIsDetailsModalOpen}
                    setIsBulkModalOpen={setIsBulkModalOpen}
                    setAllDeliveries={setAllDeliveries}
                    devicesList={devicesList}
                    isLoading={
                        apiFetchInProgress.current ||
                        isDeviceListLoading ||
                        isIssuesLoading
                    }
                    onDoneSelected={(ticket: Ticket) => {
                        setTicketsToResolve([ticket]);
                        setIsResolveModalOpen(true);
                    }}
                />
            )}
            {currentView === 'week' && (
                <DeliveriesWeekView
                    deliveries={deliveries}
                    selectedDate={addDays(startOfWeek(minDate), 1)}
                    changeWeek={changeWeek}
                    setActiveTicket={setActiveTicket}
                    selectedTickets={selectedTickets}
                    setSelectedTickets={setSelectedTickets}
                    setIsBulkModalOpen={setIsBulkModalOpen}
                    setAllDeliveries={setAllDeliveries}
                    devicesList={devicesList}
                    isLoading={
                        apiFetchInProgress.current ||
                        isDeviceListLoading ||
                        isIssuesLoading
                    }
                />
            )}
            {currentView === 'day' && (
                <UnderConstructionPlaceholder
                    name="Planning Day View"
                    explanation="This will allow planning of the day's deliveries, eventually including scheduling, managing drivers, and planning routes."
                />
            )}
            {activeTicket && (
                <DeliveryDetailsModal
                    modalOpen={isDetailsModalOpen}
                    setModalOpen={setIsDetailsModalOpen}
                    ticket={activeTicket}
                    setActiveTicket={setActiveTicket}
                    setDeliveries={setDeliveries}
                    device={devicesList[activeTicket.deviceId]}
                />
            )}
            <BulkStatusModal
                isOpen={isBulkModalOpen}
                close={() => setIsBulkModalOpen(false)}
                submit={(status: string) => bulkUpdateStatuses(status)}
            />
            <CreateDeliveryModal
                modalOpen={isCreateModalOpen}
                setModalOpen={setIsCreateModalOpen}
                deliveries={deliveries}
                setAllDeliveries={setAllDeliveries}
                devicesList={devicesList}
                setDevicesList={setDevicesList}
                viewAsSupplier={supplierId ?? ''}
            />
            <DeliveriesPrintModal
                deliveries={allDeliveries}
                selectedTickets={selectedTickets}
                modalOpen={isPrintModalOpen}
                setModalOpen={setIsPrintModalOpen}
                viewAsSupplier={supplierId ?? ''}
                devicesList={devicesList}
            />
            <ResolveDeliveryModal
                modalOpen={isResolveModalOpen}
                setModalOpen={setIsResolveModalOpen}
                ticketsToResolve={ticketsToResolve}
                devicesList={devicesList}
                resetTickets={(updatedTickets: Ticket[]) => {
                    setSelectedTickets([]);
                    setTicketsToResolve([]);
                    const newAllDeliveries = [...allDeliveries];
                    updatedTickets.forEach((ticket) => {
                        const index = findIndex(allDeliveries, {
                            id: ticket.id,
                        });
                        newAllDeliveries.splice(index, 1, ticket);
                    });
                    setAllDeliveries(newAllDeliveries);
                }}
            />
        </div>
    );
};
